import { useState } from 'react';

import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import { SelectedUserStatusesEnum } from '../constants';
import { BulkTicketFormValues, SelectedUser } from '../types';

import { useSelectedUsers } from './useSelectedUsers';
import { useTelegramValidation } from './useTelegramValidation';
import { useUserSearch } from './useUserSearch';

import { chanceTicketService } from 'v2/api/chanceTicketService';
import { UserModel } from 'v2/types/UserModel';

export const useBulkDistribution = (ticketId: string) => {
  const [isDistributing, setIsDistributing] = useState(false);

  const {
    searchQuery: userSearchQuery,
    setSearchQuery: setUserSearchQuery,
    usersData,
    isUsersLoading,
    clearSearch,
  } = useUserSearch();

  const { selectedUsers, addUser, addUsers, removeUser, updateUserStatus, resetAllStatuses, clearUsers } =
    useSelectedUsers();

  const { validateUsernames, isValidating } = useTelegramValidation();
  const { trigger: bulkCreateChanceTickets } = useSWRMutation(
    'bulkCreateChanceTickets',
    chanceTicketService.bulkCreate
  );

  const handleAddUser = (user: UserModel) => {
    addUser(user);
    clearSearch();
  };

  const handleAddUsersByTelegram = (users: SelectedUser[]) => {
    addUsers(users);
  };

  const handleDistribute = async (values: BulkTicketFormValues) => {
    const validUsers = selectedUsers.filter((user) => user.status !== SelectedUserStatusesEnum.NOT_FOUND);

    if (validUsers.length === 0) {
      toast.error('No valid users for ticket distribution');
      return;
    }

    const notFoundUsers = selectedUsers.filter((user) => user.status === SelectedUserStatusesEnum.NOT_FOUND);
    if (notFoundUsers.length > 0) {
      toast.warning(`${notFoundUsers.length} users not found and will be skipped`);
    }

    setIsDistributing(true);
    resetAllStatuses(SelectedUserStatusesEnum.PENDING);

    let successCount = 0;
    let errorCount = 0;

    for (const user of validUsers) {
      try {
        await bulkCreateChanceTickets({
          userId: user.id,
          entityId: ticketId,
          quantity: values.quantity,
        });

        updateUserStatus(user.id, SelectedUserStatusesEnum.SUCCESS);
        successCount++;
      } catch (error: any) {
        const errorMessage = error?.data?.message || 'Unknown error';
        updateUserStatus(user.id, SelectedUserStatusesEnum.ERROR, errorMessage);
        errorCount++;
      }
    }

    setIsDistributing(false);

    if (successCount > 0) {
      toast.success(`Tickets successfully distributed to ${successCount} users`);
    }

    if (errorCount > 0) {
      toast.error(`Error distributing tickets to ${errorCount} users`);
    }
  };

  const resetState = () => {
    clearUsers();
    clearSearch();
  };

  return {
    selectedUsers,
    userSearchQuery,
    setUserSearchQuery,
    isDistributing,
    usersData,
    isUsersLoading,
    isValidating,
    handleAddUser,
    handleAddUsersByTelegram,
    handleRemoveUser: removeUser,
    handleDistribute,
    validateUsernames,
    resetState,
  };
};
